import {useMemo} from 'react'
import {Empty, Space, Table, Typography} from 'antd'
import {EditOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {useIntl} from 'react-intl'

const SalesCodeTable = ({
  dataSource,
  pagination = false,
  getSalesCodeListData = null,
  onEdit = null,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'salesCodes.name' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'name',
        width: 150,
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'salesCodes.description' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'description',
        sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
        render: (text) => (
            <EllipsisTooltip className="multiline-ellipsis" lineClamp={2}>
              {text || '-'}
            </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'salesCodes.group' }),
        dataIndex: 'group_display',
        width: 140,
        sorter: (a, b) => (a.group_display || '').localeCompare(b.group_display || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'salesCodes.groupDescription' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'group_description_display',
        sorter: (a, b) =>
          (a.group_description_display || '').localeCompare(b.group_description_display || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'salesCodes.default' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'default_display',
        width: 120,
        align: 'center',
        filters: [
          { text: intl.formatMessage({ id: 'salesCodes.yes' }), value: true },
          { text: intl.formatMessage({ id: 'salesCodes.no' }), value: false },
        ],
        onFilter: (value, record) => record.default === value,
        render: (_, record) => (
          <Typography.Text>
            {record.default
              ? intl.formatMessage({ id: 'salesCodes.yes' })
              : intl.formatMessage({ id: 'salesCodes.no' })}
          </Typography.Text>
        ),
      },
      {
        title: intl.formatMessage({ id: 'salesCodes.action' }),
        align: 'right',
        width: 100,
        render: (_, record) => (
          <TableActions
            actions={[
              { key: 'edit', label: intl.formatMessage({ id: 'salesCodes.edit' }), icon: <EditOutlined />, onClick: () => onEdit?.(record) },
            ]}
          />
        ),
      },
    ],
    [dataSource, pagination]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `sales-code-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        tableLayout='fixed'
        scroll={dataSource?.length ? { x: 900 } : undefined}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getSalesCodeListData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default SalesCodeTable
